// Heal outcomes the UI shows apart from a plain status (DPS-4717).
//
// Two kinds of job end as status COMPLETED with no PR, and neither is a heal:
// - Out of scope: the healing agent's eligibility gate decided it can't heal a
//   retailer's config. Result "OUT_OF_SCOPE — <reason>"; newer agents also set
//   review_context.out_of_scope = true.
// - False positive: the existing config already extracts correctly, so the
//   anomaly was not real. Result "FALSE_POSITIVE — <reason>"; newer agents also
//   set review_context.false_positive = true.
// The Job Tracker shows them as "Out of scope" / "False positive", not "Completed".
// The agent's status lookup reports them as OUT_OF_SCOPE / FALSE_POSITIVE
// (lowercased by the backend), so the Anomalies tab never shows them as "Healed".

export const OUT_OF_SCOPE = 'out_of_scope';
export const FALSE_POSITIVE = 'false_positive';

const OUT_OF_SCOPE_RE = /^\s*OUT_OF_SCOPE\s*[—–-]\s*([\s\S]*)$/;
const FALSE_POSITIVE_RE = /^\s*FALSE_POSITIVE\s*[—–-]\s*([\s\S]*)$/;
const OUT_OF_SCOPE_GENERIC = "The healing agent can't heal this retailer's config; no repair was attempted";
const FALSE_POSITIVE_GENERIC = 'The config already extracts correctly; the anomaly was not a real one';

const IN_FLIGHT = ['pending', 'running', 'awaiting_review'];

function completedReason(job, re, flag, generic) {
  if (!job || String(job.status || '').toLowerCase() !== 'completed') return null;
  const text = [job.result, job.message].find((t) => typeof t === 'string' && re.test(t));
  if (text) return text.match(re)[1].trim() || generic;
  return job.review_context?.[flag] === true ? generic : null;
}

/** The agent's reason for an out-of-scope job, or null when the job is not out of scope. */
export function outOfScopeReason(job) {
  return completedReason(job, OUT_OF_SCOPE_RE, 'out_of_scope', OUT_OF_SCOPE_GENERIC);
}

/** The agent's reason for a false-positive job, or null. A job that raised a PR is never one. */
export function falsePositiveReason(job) {
  if (job?.review_context?.pr_url || job?.review_context?.pr_number) return null;
  if (outOfScopeReason(job) !== null) return null;
  return completedReason(job, FALSE_POSITIVE_RE, 'false_positive', FALSE_POSITIVE_GENERIC);
}

/** Status used for the Job Tracker badge and status filter. */
export function jobDisplayStatus(job) {
  if (outOfScopeReason(job) !== null) return OUT_OF_SCOPE;
  if (falsePositiveReason(job) !== null) return FALSE_POSITIVE;
  return job?.status;
}

/** "Out of scope: <reason>" / "False positive: <reason>" for the Job Tracker row, or null. */
export function jobReasonText(job) {
  const oos = outOfScopeReason(job);
  if (oos !== null) return `Out of scope: ${oos}`;
  const fp = falsePositiveReason(job);
  return fp !== null ? `False positive: ${fp}` : null;
}

/** Which heal chip an anomaly row shows: 'healed', 'healing', 'out_of_scope', 'false_positive' or null. */
export function anomalyHealChip(healStatus) {
  if (healStatus === 'completed') return 'healed';
  if (healStatus === OUT_OF_SCOPE) return OUT_OF_SCOPE;
  if (healStatus === FALSE_POSITIVE) return FALSE_POSITIVE;
  if (IN_FLIGHT.includes(healStatus)) return 'healing';
  return null;
}

/**
 * Heal can be triggered only when no heal chip shows. An out-of-scope retry is
 * gated out again; a false positive means the agent found the anomaly is not
 * real (the config already works), so there is nothing to heal.
 */
export function canTriggerHeal(healStatus) {
  return anomalyHealChip(healStatus) === null;
}
